import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useAcceptInviteMutation } from '@/redux/service/userManagement'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Lock,
  ArrowRight,
  Loader2,
  EyeOff,
  Eye,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react'
import logo from '@/assets/logo.png'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'

const AcceptInvite = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [acceptInvite, { isLoading }] = useAcceptInviteMutation()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
          /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).+$/,
          'Password must contain at least 1 uppercase letter and 1 symbol'
        )
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required'),
    }),
    onSubmit: async values => {
      if (!token) {
        toast.error('Invalid or missing invitation token')
        return
      }

      try {
        await acceptInvite({
          token,
          password: values.password,
          type: 1,
        }).unwrap()

        toast.success('Account activated successfully!')
        setTimeout(() => {
          navigate('/login')
        }, 1000)
      } catch (err) {
        const error = err as { data?: { message?: string } }
        toast.error(error?.data?.message ?? 'Failed to activate account')
      }
    },
  })

  if (!token) {
    return (
      <div className='min-h-screen bg-white flex items-center justify-center p-4 selection:bg-black selection:text-white font-sans'>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className='max-w-md w-full text-center'
        >
          <div className='w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-100 shadow-sm'>
            <AlertCircle className='w-10 h-10 text-red-500' />
          </div>
          <h1 className='text-3xl font-bold text-black mb-2 tracking-tight'>
            Invalid Link
          </h1>
          <p className='text-slate-500 mb-8 font-medium leading-relaxed'>
            The invitation link you followed is invalid or has expired.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className='w-full h-12 bg-black hover:bg-slate-800 text-white font-bold rounded-lg shadow-xl shadow-black/10 transition-all active:scale-[0.98]'
          >
            Return to Login
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className='min-h-screen w-full flex bg-white font-sans selection:bg-black selection:text-white'>
      {/* Visual Side (Hidden on Mobile) */}
      <div className='hidden lg:flex w-1/2 bg-black flex-col justify-center gap-10 p-12 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-20 pointer-events-none'>
          <div className='absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-white blur-[120px]' />
          <div className='absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-slate-800 blur-[120px]' />
        </div>

        <div className='relative z-10 flex items-center gap-2 group cursor-default'>
          <div className='w-10 h-10 rounded-lg bg-white flex items-center justify-center transition-transform group-hover:scale-110 shadow-lg shadow-white/10'>
            <img src={logo} alt='logo' className='w-6 h-6' />
          </div>
          <span className='text-2xl font-bold tracking-tighter'>
            ClickVoltz<span className='text-[#FFC107]'>.</span>
          </span>
        </div>

        <div className='relative z-10 max-w-md'>
          <h1 className='text-5xl font-extrabold tracking-tight leading-[1.1] mb-6'>
            Activate <br />
            <span className='text-slate-400'>Your Account.</span>
          </h1>
          <p className='text-lg text-slate-400 font-medium'>
            Set up your secure password to access your workspace and start
            collaborating.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className='w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50/30'>
        <div className='w-full max-w-[400px] animate-in fade-in slide-in-from-bottom-4 duration-1000'>
          <Card className='border-none shadow-none bg-transparent'>
            <CardHeader className='p-0 mb-8 space-y-2'>
              <div className='lg:hidden flex items-center gap-2 mb-8'>
                <div className='w-8 h-8 rounded-lg bg-black flex items-center justify-center shadow-lg shadow-black/10'>
                  <img src={logo} alt='logo' className='w-5 h-5' />
                </div>
                <span className='text-xl font-bold tracking-tighter text-black'>
                  ClickVoltz
                </span>
              </div>
              <CardTitle className='text-3xl text-center font-bold tracking-tight text-black '>
                Activate Account
              </CardTitle>
              <CardDescription className='text-slate-500 font-medium text-center'>
                Join your team by creating a secure password
              </CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              <form onSubmit={formik.handleSubmit} className='space-y-6'>
                <div className='space-y-4'>
                  {/* Password */}
                  <div className='space-y-2 group'>
                    <Label
                      htmlFor='password'
                      className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                    >
                      New Password <span className='text-red-500'>*</span>
                    </Label>
                    <div className='relative'>
                      <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                      <Input
                        id='password'
                        type={showPassword ? 'text' : 'password'}
                        placeholder='••••••••'
                        className={`pl-10 pr-10 h-11 bg-white text-black rounded-lg border transition-all
                          focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                        ${
                          formik.touched.password && formik.errors.password
                            ? 'border-red-300 focus:ring-red-100 focus:border-red-300'
                            : 'border-slate-200'
                        }`}
                        {...formik.getFieldProps('password')}
                        disabled={isLoading}
                      />
                      <button
                        type='button'
                        onClick={() => setShowPassword(prev => !prev)}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black transition-colors'
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <Eye className='w-4 h-4' />
                        ) : (
                          <EyeOff className='w-4 h-4' />
                        )}
                      </button>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <p className='text-xs font-bold text-red-500 '>
                        {formik.errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className='space-y-2 group'>
                    <Label
                      htmlFor='confirmPassword'
                      className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                    >
                      Confirm Password <span className='text-red-500'>*</span>
                    </Label>
                    <div className='relative'>
                      <CheckCircle2 className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                      <Input
                        id='confirmPassword'
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder='••••••••'
                        className={`pl-10 pr-10 h-11 bg-white text-black rounded-lg border transition-all
                          focus:outline-none focus:ring-2 focus:ring-black focus:border-black
                        ${
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                            ? 'border-red-300 focus:ring-red-100 focus:border-red-300'
                            : 'border-slate-200'
                        }`}
                        {...formik.getFieldProps('confirmPassword')}
                        disabled={isLoading}
                      />
                      <button
                        type='button'
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black transition-colors'
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className='w-4 h-4' />
                        ) : (
                          <Eye className='w-4 h-4' />
                        )}
                      </button>
                    </div>
                    {formik.touched.confirmPassword &&
                      formik.errors.confirmPassword && (
                        <p className='text-xs font-bold text-red-500   px-1 '>
                          {formik.errors.confirmPassword}
                        </p>
                      )}
                  </div>
                </div>

                <Button
                  type='submit'
                  className='w-full h-11 bg-black hover:bg-slate-800 text-white font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 shadow-xl shadow-black/10 mt-2'
                  disabled={isLoading || !formik.isValid || !formik.dirty}
                >
                  {isLoading ? (
                    <Loader2 className='w-5 h-5 animate-spin' />
                  ) : (
                    <span className='flex items-center gap-2'>
                      Activate Account <ArrowRight className='w-4 h-4' />
                    </span>
                  )}
                </Button>
              </form>

              <div className='text-center mt-5'>
                <button
                  onClick={() => navigate('/login')}
                  className='text-xs font-bold text-slate-400 hover:text-black transition-all hover:tracking-wide'
                >
                  Return To Login
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
